import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpIcon, ArrowDownIcon, ExternalLinkIcon } from 'lucide-react';
import Button from '../common/Button';
import { AuthContext } from '../../context/AuthContext';
import { useUserProfile } from '../../hooks/useUserProfile';
import SignInPromptModal from './modals/SignInPromptModal';
import OrgConfirmModal from './modals/OrgConfirmModal';
import OrgSelectModal from './modals/OrgSelectModal';
import { supabase } from '../../services/supabase';
import CampaignFallbackImage from './CampaignFallbackImage';
import { trackEvent, ANALYTICS_EVENTS } from '../../utils/analytics';
import { getSourceFallback, GENERIC_FALLBACK_IMAGE } from '../../utils/sourcecFallbacks';
import { shouldShowVoteCounts } from '../../utils/voteDisplay';

interface CampaignCardProps {
  id: string;
  image: string;
  title: string;
  author: string;
  source?: string;
  sourceUrl?: string;
  date: Date;
  description: string;
  ageLabel?: string;
  category?: string;
  isSponsored?: boolean;
  sponsorName?: string;
  sponsorUrl?: string;
}

const CampaignCard: React.FC<CampaignCardProps> = ({
  id,
  image,
  title,
  author,
  source,
  sourceUrl,
  date,
  description,
  ageLabel,
  category,
  isSponsored = false,
  sponsorName,
  sponsorUrl,
}) => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { profile: userProfile } = useUserProfile();

  const [showPrompt, setShowPrompt] = useState(false);
  const [showOrgConfirm, setShowOrgConfirm] = useState(false);
  const [showOrgSelect, setShowOrgSelect] = useState(false);
  const [pendingAction, setPendingAction] = useState<'support' | 'oppose' | null>(null);
  const [userVote, setUserVote] = useState<'upvote' | 'downvote' | null>(null);
  const [upvoteCount, setUpvoteCount] = useState(0);
  const [downvoteCount, setDownvoteCount] = useState(0);
  const [imgFailed, setImgFailed] = useState(false);

  const normalizedImage = useMemo(() => (image || '').trim().toLowerCase(), [image]);

  const normalizedGenericFallback = useMemo(
    () => GENERIC_FALLBACK_IMAGE.trim().toLowerCase(),
    []
  );

  const normalizedSourceFallback = useMemo(() => {
    const fallback = getSourceFallback(source);
    return fallback ? fallback.trim().toLowerCase() : null;
  }, [source]);

  const isRemotePlaceholderFallback = useMemo(() => {
    return normalizedImage.includes('placehold.co/') && normalizedImage.includes('text=ballotr');
  }, [normalizedImage]);

  const isFallbackAsset = useMemo(() => {
    if (!normalizedImage) return false;

    return (
      normalizedImage === normalizedGenericFallback ||
      (!!normalizedSourceFallback && normalizedImage === normalizedSourceFallback) ||
      isRemotePlaceholderFallback
    );
  }, [
    normalizedImage,
    normalizedGenericFallback,
    normalizedSourceFallback,
    isRemotePlaceholderFallback,
  ]);

  const hasImage = useMemo(() => !!image && image.trim().length > 5, [image]);
  const showFallback = !hasImage || imgFailed || isFallbackAsset;
  const showVoteCounts = shouldShowVoteCounts({
    userVote,
    upvotes: upvoteCount,
    downvotes: downvoteCount,
  });

  const decodeHtml = (text?: string) => {
    if (!text) return '';
    const txt = document.createElement('textarea');
    txt.innerHTML = text;
    return txt.value;
  };

  const cleanDescription = decodeHtml(description);

  const isBadSnippet = (text?: string) => {
    const t = (text ?? '').trim().toLowerCase();
    if (!t) return true;
    if (t === 'read more' || t === 'read more.' || t === 'read more…' || t === 'read more...') {
      return true;
    }
    if (t.length < 40) return true;
    return false;
  };

  useEffect(() => {
    setImgFailed(false);
  }, [image, id]);

  useEffect(() => {
    const fetchVotes = async () => {
      const { data, error } = await supabase
        .from('campaigns')
        .select('upvotes, downvotes')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Vote fetch error:', error);
        return;
      }

      if (!data) {
        console.error('Vote fetch returned no campaign row for id:', id);
        return;
      }

      setUpvoteCount(data.upvotes);
      setDownvoteCount(data.downvotes);
    };

    fetchVotes();
  }, [id]);

  useEffect(() => {
    if (!user) {
      setUserVote(null);
      return;
    }

    (async () => {
      const { data: myVote, error } = await supabase
        .from('votes')
        .select('vote_type')
        .eq('campaign_id', id)
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Vote fetch error:', error);
        return;
      }

      setUserVote((myVote?.vote_type as 'upvote' | 'downvote') ?? null);
    })();
  }, [id, user]);

  const handleVote = async (type: 'upvote' | 'downvote') => {
    trackEvent(ANALYTICS_EVENTS.CAMPAIGN_VOTE_CLICKED, {
      campaign_id: id,
      vote_type: type,
      location: 'home',
    });

    if (!user) {
      trackEvent(ANALYTICS_EVENTS.SIGNIN_PROMPT_SHOWN, { campaign_id: id });
      setShowPrompt(true);
      return;
    }

    const sameClick = userVote === type;
    const prev = userVote;

    if (sameClick) {
      setUserVote(null);

      const { error } = await supabase
        .from('votes')
        .delete()
        .eq('campaign_id', id)
        .eq('user_id', user.id);

      if (error) {
        console.error('Vote delete error:', error);
        setUserVote(prev);
        return;
      }
    } else {
      setUserVote(type);

      const { error } = await supabase
        .from('votes')
        .upsert(
          {
            campaign_id: id,
            user_id: user.id,
            vote_type: type,
          },
          { onConflict: 'campaign_id,user_id' }
        );

      if (error) {
        console.error('Vote upsert error:', error);
        setUserVote(prev);
        return;
      }
    }

    const { data, error: refreshError } = await supabase
      .from('campaigns')
      .select('upvotes, downvotes')
      .eq('id', id)
      .single();

    if (refreshError) {
      console.error('Vote refresh error:', refreshError);
      return;
    }

    if (!data) {
      console.error('Vote refresh returned no campaign row for id:', id);
      return;
    }

    setUpvoteCount(data.upvotes);
    setDownvoteCount(data.downvotes);
  };

  const proceedWithSavedOrg = () => {
    if (!pendingAction) return;
    setShowOrgConfirm(false);
    navigate(`/campaign/${id}?action=${pendingAction}`);
  };

  const handleActionClick = (action: 'support' | 'oppose') => {
    trackEvent(
      action === 'support'
        ? ANALYTICS_EVENTS.CAMPAIGN_SUPPORT_CLICKED
        : ANALYTICS_EVENTS.CAMPAIGN_OPPOSE_CLICKED,
      {
        campaign_id: id,
        location: 'home',
      }
    );

    if (!user) {
      trackEvent(ANALYTICS_EVENTS.SIGNIN_PROMPT_SHOWN, { campaign_id: id });
      setShowPrompt(true);
      return;
    }

    setPendingAction(action);

    if (userProfile?.preferred_org) {
      trackEvent(ANALYTICS_EVENTS.SAVED_ORG_CONFIRM_SHOWN, {
        campaign_id: id,
        org_id: userProfile.preferred_org.id,
      });

      setShowOrgConfirm(true);
      return;
    }

    trackEvent(ANALYTICS_EVENTS.ORG_SELECT_MODAL_SHOWN, {
      campaign_id: id,
    });

    setShowOrgSelect(true);
  };

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;

    if (!target.closest('button') && !target.closest('a')) {
      trackEvent(ANALYTICS_EVENTS.CAMPAIGN_CARD_CLICKED, { campaign_id: id });
      navigate(`/campaign/${id}`);
    }
  };

  const handleSourceClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    trackEvent(ANALYTICS_EVENTS.CAMPAIGN_SOURCE_CLICKED, {
      campaign_id: id,
      source,
      source_url: sourceUrl,
      location: 'home',
    });
  };

  return (
    <>
      <div
        className="bg-white shadow-md rounded-lg overflow-hidden flex flex-col cursor-pointer"
        onClick={handleCardClick}
      >
        <div className="relative">
          {sourceUrl ? (
            <a
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleSourceClick}
            >
              {showFallback ? (
                <CampaignFallbackImage
                  title={decodeHtml(title)}
                  category={category}
                  source={source}
                />
              ) : (
                <img
                  src={image}
                  alt={decodeHtml(title)}
                  className="h-48 w-full object-cover"
                  onError={() => setImgFailed(true)}
                />
              )}

              {isSponsored && (
                <div className="absolute top-2 left-2">
                  <div className="px-2 py-1 rounded-full text-xs font-semibold bg-white/90 border border-gray-200 shadow-sm">
                    Sponsored
                  </div>
                </div>
              )}

              <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 p-1 rounded-full">
                <ExternalLinkIcon className="text-white w-4 h-4" />
              </div>
            </a>
          ) : (
            <>
              {showFallback ? (
                <CampaignFallbackImage
                  title={decodeHtml(title)}
                  category={category}
                  source={source}
                />
              ) : (
                <img
                  src={image}
                  alt={decodeHtml(title)}
                  className="h-48 w-full object-cover"
                  onError={() => setImgFailed(true)}
                />
              )}

              {isSponsored && (
                <div className="absolute top-2 left-2">
                  <div className="px-2 py-1 rounded-full text-xs font-semibold bg-white/90 border border-gray-200 shadow-sm">
                    Sponsored
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex-1 flex flex-col p-4">
          <h3 className="text-lg font-semibold text-textPrimary mb-1 break-words leading-snug">
            {decodeHtml(title)}
          </h3>

          <p className="text-sm text-textSecondary">
            {source ? `Source: ${source}` : '—'} • {ageLabel || '—'}
          </p>

          {!isBadSnippet(cleanDescription) ? (
            <p className="text-sm text-textPrimary mb-4 line-clamp-3">
              {decodeHtml(cleanDescription)}
            </p>
          ) : (
            <p className="text-sm text-textSecondary mb-4 italic">
              Open the full story at {source || 'the source'}.
            </p>
          )}

          <div className="mt-auto border-t border-gray-150 pt-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={userVote === 'upvote' ? undefined : 'outline'}
                  size="sm"
                  onClick={() => handleVote('upvote')}
                  className="min-w-[42px]"
                >
                  <ArrowUpIcon className={`w-4 h-4 inline ${showVoteCounts ? 'mr-1' : ''}`} />
                  {showVoteCounts && upvoteCount}
                </Button>

                <Button
                  variant={userVote === 'downvote' ? undefined : 'outline'}
                  size="sm"
                  onClick={() => handleVote('downvote')}
                  className="min-w-[42px]"
                >
                  <ArrowDownIcon className={`w-4 h-4 inline ${showVoteCounts ? 'mr-1' : ''}`} />
                  {showVoteCounts && downvoteCount}
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button variant="support" onClick={() => handleActionClick('support')}>
                  Support
                </Button>

                <Button variant="secondary" onClick={() => handleActionClick('oppose')}>
                  Oppose
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showPrompt && <SignInPromptModal onClose={() => setShowPrompt(false)} />}

      {showOrgConfirm && userProfile?.preferred_org && (
        <OrgConfirmModal
          savedOrg={userProfile.preferred_org}
          onProceed={proceedWithSavedOrg}
          onChange={() => {
            setShowOrgConfirm(false);
            setShowOrgSelect(true);
          }}
          onClose={() => setShowOrgConfirm(false)}
        />
      )}

      {showOrgSelect && pendingAction && (
        <OrgSelectModal
          isOpen={true}
          onClose={() => setShowOrgSelect(false)}
          campaignId={id}
          action={pendingAction}
        />
      )}
    </>
  );
};

export default CampaignCard;